import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import { ContractService } from "@services/finance/contracts/contractService";
import { Contract } from "@models/contract.model";
import { Pricesheet } from "@models/pricesheet.model";
import ContractEditor from "../components/ContractEditor";
import useOrganizationContracts from "src/modules/organizations/contracts/hooks/useOrganizationContracts";
import { Paths } from "src/paths";
import { readReturnTo, withReturnTo } from "src/shared/utils/returnTo";

const contractService = new ContractService();

/*
    A contract by URL. The same editor, store and mutations as the organization
    Contracts tab and the engagement Contract tab — not a read-only copy of them.
    Before MPN-1714 this page rendered the bare detail card with no handlers, so
    an operator returning here from a price sheet found the delete and create
    buttons they had just used were gone.
*/
const ContractDetailPage: FC = () => {
  const { contractId: contractIdParam } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const contractId = contractIdParam ? parseInt(contractIdParam, 10) : null;

  // The store is keyed by organization, and the organization lives on the
  // contract, so the contract itself is fetched first.
  const [loaded, setLoaded] = useState<Contract | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (contractId == null) return;
    setIsLoading(true);
    setError(null);
    contractService
      .get(contractId)
      .then((c) => (c ? setLoaded(c) : setError("Contract not found.")))
      .catch(() => setError("Failed to load contract."))
      .finally(() => setIsLoading(false));
  }, [contractId]);

  const org = useOrganizationContracts(loaded?.client_org_id ?? 0);

  // The hook defaults to the organization's first contract; point it at this one.
  // Not while a mutation runs: deleting this contract moves the selection off it
  // before onContractDeleted navigates away, and re-selecting it would 404.
  useEffect(() => {
    if (loaded && contractId != null && org.selectedContractId !== contractId && !org.isSaving) {
      org.selectContract(contractId);
    }
  }, [loaded, contractId, org.selectedContractId, org.isSaving]);

  const contract = org.contracts.find((c) => c.id === contractId) ?? loaded;

  const orgHref = (orgId: number) =>
    `${import.meta.env.BASE_URL}${Paths.ORGANIZATION_DETAIL}/${orgId}?tab=contracts`;

  // Where this page was entered from — an engagement stays an engagement
  // (MPN-1715). By URL alone, the organization's Contracts tab.
  const from = readReturnTo(location.search);
  const backHref = (orgId: number) => from ?? orgHref(orgId);
  const backLabel = from?.includes(`/${Paths.ENGAGEMENT}/`) ? "Back to engagement" : "Back to organization";

  const handleEditPricesheet = (ps: Pricesheet) => {
    if (contractId == null) return;
    // This page, `from` included, so the sheet's Back returns here and this
    // page's Back still knows where the operator started.
    navigate(
      withReturnTo(
        `${import.meta.env.BASE_URL}${Paths.CONTRACT_PRICESHEET}`
          .replace(":contractId", String(contractId))
          .replace(":pricesheetId", String(ps.id)),
        `${location.pathname}${location.search}`,
      ),
    );
  };

  if (error) {
    return (
      <div className="container-fluid py-4">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }
  if (isLoading || !contract || org.selectedContractId !== contractId) return <Loader />;

  return (
    <div className="container-fluid py-4">
      <div className="mb-3">
        <Button
          variant="link"
          className="p-0 text-decoration-none text-muted"
          onClick={() => navigate(backHref(contract.client_org_id))}
        >
          <i className="bi bi-arrow-left me-1" />
          {backLabel}
        </Button>
      </div>
      <ContractEditor
        contract={contract}
        budgets={org.budgets}
        pricesheets={org.pricesheets}
        pricesheetItems={org.pricesheetItems}
        spendAccount={org.spendAccount}
        credits={org.credits}
        debits={org.debits}
        balance={org.balance}
        organizationId={contract.client_org_id}
        isSaving={org.isSaving}
        title={contract.name?.trim() || `Contract #${contract.id}`}
        mutations={{
          updateContract: org.updateContract,
          deleteContract: org.deleteContract,
          createBudget: org.createBudget,
          updateBudget: org.updateBudget,
          deleteBudget: org.deleteBudget,
          savePricesheet: org.savePricesheet,
          deletePricesheet: org.deletePricesheet,
          createCredit: org.createCredit,
          updateCredit: org.updateCredit,
          deleteCredit: org.deleteCredit,
          createDebit: org.createDebit,
          updateDebit: org.updateDebit,
          deleteDebit: org.deleteDebit,
        }}
        onEditPricesheet={handleEditPricesheet}
        onContractDeleted={() => navigate(backHref(contract.client_org_id))}
      />
    </div>
  );
};

export default ContractDetailPage;
